import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ComponentRef, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin, Subject } from 'rxjs';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { FormMode } from 'src/app/controls/common/form-mode';
import { NeedApiConfigurations } from 'src/app/controls/workflows/need-api-configurations';
import { NeedAttributes } from 'src/app/controls/workflows/need-attributes';
import { NeedCustomers } from 'src/app/controls/workflows/need-customers';
import { NeedEmailTemplates } from 'src/app/controls/workflows/need-email-templates';
import { NeedFolderStates } from 'src/app/controls/workflows/need-folder-states';
import { NeedSmsTemplates } from 'src/app/controls/workflows/need-sms-templates';
import { NeedSteps } from 'src/app/controls/workflows/need-steps';
import { NeedTimerTriggers } from 'src/app/controls/workflows/need-timer-triggers';
import { NeedUsers } from 'src/app/controls/workflows/need-users';
import { WorkflowActionActivateStepDataComponent } from 'src/app/controls/workflows/workflow-action-activate-step-data/workflow-action-activate-step-data.component';
import { WorkflowActionArchiveFolderDataComponent } from 'src/app/controls/workflows/workflow-action-archive-folder-data/workflow-action-archive-folder-data.component';
import { WorkflowActionCallApiDataComponent } from 'src/app/controls/workflows/workflow-action-call-api-data/workflow-action-call-api-data.component';
import { WorkflowActionChangeStepStateDataComponent } from 'src/app/controls/workflows/workflow-action-change-step-state-data/workflow-action-change-step-state-data.component';
import { WorkflowActionCompleteStepDataComponent } from 'src/app/controls/workflows/workflow-action-complete-step-data/workflow-action-complete-step-data.component';
import { WorkflowActionCompleteWorkflowDataComponent } from 'src/app/controls/workflows/workflow-action-complete-workflow-data/workflow-action-complete-workflow-data.component';
import { WorkflowActionReminderCreationDataComponent } from 'src/app/controls/workflows/workflow-action-reminder-creation-data/workflow-action-reminder-creation-data.component';
import { WorkflowActionSendMailDataComponent } from 'src/app/controls/workflows/workflow-action-send-mail-data/workflow-action-send-mail-data.component';
import { WorkflowActionSendSmsDataComponent } from 'src/app/controls/workflows/workflow-action-send-sms-data/workflow-action-send-sms-data.component';
import { WorkflowActionSwitchFolderAsUnreadDataComponent } from 'src/app/controls/workflows/workflow-action-switch-folder-as-unread-data/workflow-action-switch-folder-as-unread-data.component';
import { WorkflowActionTimerStopComponent } from 'src/app/controls/workflows/workflow-action-timer-stop/workflow-action-timer-stop.component';
import { WorkflowActionUpdateAttributeDataComponent } from 'src/app/controls/workflows/workflow-action-update-attribute-data/workflow-action-update-attribute-data.component';
import { WorkflowDataInterface } from 'src/app/controls/workflows/workflow-data.interface';
import { WorkflowNotImplementedActionDataComponent } from 'src/app/controls/workflows/workflow-not-implemented-action-data.component/workflow-not-implemented-action-data.component';
import { ContextOfUse } from 'src/app/models/RestApiCaller/Enums/context-of-use';
import { WorkflowActionTypes } from 'src/app/models/RestApiCaller/Enums/workflow-action-types';
import { WorkflowTriggerTypes } from 'src/app/models/RestApiCaller/Enums/workflow-trigger-types';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { CustomersService } from 'src/app/services/customers.service';
import { EmailsTemplatesService } from 'src/app/services/emails-templates.service';
import { FoldersStatesService } from 'src/app/services/folders-states.service';
import { MemoryService } from 'src/app/services/memory.service';
import { RestApiConfigurationsService } from 'src/app/services/restApi/rest-api-configurations.service';
import { SmsTemplatesService } from 'src/app/services/sms-templates.service';
import { UsersService } from 'src/app/services/users.service';
import { AnyActionData } from '../../../models/Types/any-action-data';
import { Workflow } from '../../../models/workflow';
import { IFormComponent } from '../core/form-component.interface';

/**
 * Composants affichant le formulaire d'édition de l'action de workflow selectionnée dans le designer de workflow.
 */
@Component({
    selector: 'app-workflow-action-form',
    templateUrl: './workflow-action-form.html',
    styleUrl: './workflow-action-form.scss',
    imports: [FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatOptionModule, CommonModule],
})
export class WorkflowActionFormComponent implements IFormComponent, OnInit, AfterViewInit {
    /**
     * Reference au container dans lequel est affiché les composants dynamiques.
     */
    @ViewChild('dynamicContainer', { read: ViewContainerRef })
    dynamicContainer!: ViewContainerRef;

    /**
     * Reference au composant dynamique affichant les données de l'action.
     */
    componentRef: ComponentRef<WorkflowDataInterface>;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected dynamicComponentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Données de l'action affichées dans le formulaire.
     */
    public _data: AnyActionData;

    /**
     * Spécifie les données de l'action affichées dans le formulaire.
     */
    set data(value: AnyActionData) {
        this._data = value;
        this.fillForm();
    }

    /**
     * Obtient les données de l'action affichées dans le formulaire.
     */
    get data(): AnyActionData {
        this.fillData();
        return this._data;
    }

    /**
     * Instance du formulaire.
     */
    public dataForm: UntypedFormGroup;

    /**
     * Liste des différents types d'actions affichés dans le menu déroulant.
     */
    public actionTypes: { value: WorkflowActionTypes; name: string }[] = [
        { value: WorkflowActionTypes.ActivateStep, name: 'Activer une étape' },
        { value: WorkflowActionTypes.ApiCall, name: 'Appeller une API' },
        { value: WorkflowActionTypes.ChangeStepState, name: "Changer l'état d'une étape" },
        { value: WorkflowActionTypes.CompleteStep, name: "Compléter l'étape" },
        { value: WorkflowActionTypes.CompleteWorkflow, name: 'Compléter le workflow' },
        { value: WorkflowActionTypes.OpenUrl, name: 'ouvrir une URL' },
        { value: WorkflowActionTypes.SendMail, name: 'Envoyer un mail' },
        { value: WorkflowActionTypes.SendSms, name: 'Envoyer un SMS' },
        { value: WorkflowActionTypes.SwitchFolderAsUnread, name: 'Passer le dossier en non lu' },
        { value: WorkflowActionTypes.UpdateAttribute, name: 'Mettre à jour un attribut' },
        { value: WorkflowActionTypes.TimerStop, name: 'Arrêt de minuteur' },
        { value: WorkflowActionTypes.ReminderCreation, name: 'Création de rappel' },
        { value: WorkflowActionTypes.ArchiveFolder, name: 'Archiver le dossier' },
    ];

    /**
     * Retourne une valeur indiquant si l'utilisateur a modifié des valeurs du formulaire.
     */
    get isDirty(): boolean {
        return this.dataForm.dirty || (this.componentRef && this.componentRef.instance.dirty);
    }

    /**
     * Instance du workflow.
     */
    public workflow: Workflow;

    /**
     * Constructeur.
     */
    constructor(
        private emailsTemplatesService: EmailsTemplatesService,
        private smsTemplatesService: SmsTemplatesService,
        private memoryService: MemoryService,
        private attributesService: AttributeDefinitionsService,
        private foldersStatesService: FoldersStatesService,
        private usersService: UsersService,
        protected customersService: CustomersService,
        private apiConfigurationsService: RestApiConfigurationsService,
        private spinner: NgxSpinnerService
    ) {}

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.dataForm = new UntypedFormGroup({
            id: new UntypedFormControl({
                value: '',
                disabled: true,
            }),
            type: new UntypedFormControl({ value: '', disabled: true }),
        });

        this.fillForm();
    }

    /**
     * Appellé lors de l'initialisation de la vue du composant.
     */
    ngAfterViewInit(): void {
        this.fillForm();
    }

    /**
     * Remplit le formulaire avec les données de l'action.
     */
    private fillForm() {
        if (this._data && this.dynamicContainer) {
            if (this.dataForm) {
                this.dataForm.get('id').setValue(this._data.id);
                this.dataForm.get('type').setValue(this._data.type);
            }

            this.loadDataComponent(this._data.type);
        }
    }

    /**
     * Récupère les données à partir des champs du formulaire et met à jour l'object data.
     */
    public fillData() {
        let actionData: AnyActionData = this.componentRef.instance.data as AnyActionData;

        actionData.id = this.dataForm.get('id').value;

        this._data = actionData;

        this.clearData();

        console.log('Fill data', this._data);
    }

    /**
     * Annulle les changements effectués par l'utilisateur.
     */
    public clearData() {
        this.componentRef.instance.clearData();
        this.dataForm.markAsPristine();
    }

    /**
     * Charge dynamique le composant de saisie des données de l'action.
     * @param type : Type de l'action.
     */
    private loadDataComponent(type: WorkflowActionTypes) {
        this.dynamicContainer.clear();
        this.dynamicComponentDestroyed$.next();

        if (type) {
            switch (type) {
                case WorkflowActionTypes.ChangeStepState:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionChangeStepStateDataComponent);
                    break;
                case WorkflowActionTypes.ActivateStep:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionActivateStepDataComponent);
                    break;
                case WorkflowActionTypes.SendMail:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionSendMailDataComponent);
                    break;
                case WorkflowActionTypes.SendSms:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionSendSmsDataComponent);
                    break;
                case WorkflowActionTypes.CompleteStep:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionCompleteStepDataComponent);
                    break;
                case WorkflowActionTypes.CompleteWorkflow:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionCompleteWorkflowDataComponent);
                    break;
                case WorkflowActionTypes.UpdateAttribute:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionUpdateAttributeDataComponent);
                    break;
                case WorkflowActionTypes.ApiCall:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionCallApiDataComponent);
                    break;
                case WorkflowActionTypes.SwitchFolderAsUnread:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionSwitchFolderAsUnreadDataComponent);
                    break;
                case WorkflowActionTypes.TimerStop:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionTimerStopComponent);
                    break;
                case WorkflowActionTypes.ReminderCreation:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionReminderCreationDataComponent);
                    break;
                case WorkflowActionTypes.ArchiveFolder:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowActionArchiveFolderDataComponent);
                    break;
                default:
                    this.componentRef = this.dynamicContainer.createComponent(WorkflowNotImplementedActionDataComponent);
                    console.log('WorkflowActionFormComponent - loadDataComponent --> WorkflowNotImplementedActionDataComponent');
                    break;
            }

            this.componentRef.instance.mode = FormMode.Designer;

            if (this._data) {
                this.componentRef.instance.data = this._data;
            }

            if (this.IsComponentNeedAttributes()) {
                this.loadAttributes(this.componentRef.instance as unknown as NeedAttributes);
            }

            if (this.IsComponentNeedEmailTemplates()) {
                this.loadEmailTemplates(this.componentRef.instance as unknown as NeedEmailTemplates);
            }

            if (this.IsComponentNeedSmsTemplates()) {
                this.loadSmsTemplates(this.componentRef.instance as unknown as NeedSmsTemplates);
            }

            if (this.IsComponentNeedFolderStates()) {
                this.loadFolderStates(this.componentRef.instance as unknown as NeedFolderStates);
            }

            if (this.IsComponentNeedCustomers()) {
                this.loadCustomers(this.componentRef.instance as unknown as NeedCustomers);
            }

            if (this.IsComponentNeedSteps()) {
                this.loadSteps(this.componentRef.instance as unknown as NeedSteps);
            }

            if (this.IsComponentNeedApiConfigurations()) {
                this.loadApiConfigurations(this.componentRef.instance as unknown as NeedApiConfigurations);
            }

            if (this.IsComponentNeedTimerTriggers()) {
                this.loadTimerTriggers(this.componentRef.instance as unknown as NeedTimerTriggers);
            }

            if (this.IsComponentNeedUsers()) {
                this.loadUsers(this.componentRef.instance as unknown as NeedUsers);
            }
        }
    }

    /**
     * Charge les étape de workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadSteps(componentInstance: NeedSteps) {
        componentInstance.steps = this.workflow.steps;
    }

    /**
     * Charge les attributs du workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadAttributes(componentInstance: NeedAttributes) {
        this.spinner.show('detail-bar-spinner');
        this.attributesService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let attributes = x.value.sort((a, b) => {
                return a.header < b.header ? -1 : 1;
            });
            componentInstance.attributes = attributes;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les états de dossier et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadFolderStates(componentInstance: NeedFolderStates) {
        this.spinner.show('detail-bar-spinner');
        this.foldersStatesService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let folderStates = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.folderStates = folderStates;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les utilisateurs et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadUsers(componentInstance: NeedUsers) {
        this.spinner.show('detail-bar-spinner');
        this.usersService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let users = x.value.sort((a, b) => {
                return a.displayName < b.displayName ? -1 : 1;
            });
            componentInstance.users = users;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les clients et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadCustomers(componentInstance: NeedCustomers) {
        this.spinner.show('main-spinner');
        this.customersService.List(null, null, null, null, 0, 1000).subscribe((x) => {
            let customers = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.customers = customers;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les modèles de mails et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadEmailTemplates(componentInstance: NeedEmailTemplates) {
        this.spinner.show('detail-bar-spinner');
        const viewContext: Context = {
            property: 'viewId',
            value: this.memoryService.getSelectedView().id,
        };
        const getOrganizationTemplates$ = this.emailsTemplatesService.List(null, null, null, null, 0, 1000);
        const getViewTemplates$ = this.emailsTemplatesService.List(viewContext, null, null, null, 0, 1000);

        forkJoin([getOrganizationTemplates$, getViewTemplates$]).subscribe((x) => {
            let templates = x[0].value.concat(x[1].value);
            templates = templates.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.templates = templates;
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les modèles de SMS et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadSmsTemplates(componentInstance: NeedSmsTemplates) {
        this.spinner.show('main-spinner');
        const viewContext: Context = {
            property: 'viewId',
            value: this.memoryService.getSelectedView().id,
        };
        const getOrganizationTemplates$ = this.smsTemplatesService.List(null, null, null, null, 0, 1000);
        const getViewTemplates$ = this.smsTemplatesService.List(viewContext, null, null, null, 0, 1000);

        forkJoin([getOrganizationTemplates$, getViewTemplates$]).subscribe((x) => {
            let templates = x[0].value.concat(x[1].value);
            templates = templates.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.smsTemplates = templates;
            this.spinner.hide('main-spinner');
        });
    }

    /**
     * Charge les configurations d'API de la vue et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadApiConfigurations(componentInstance: NeedApiConfigurations) {
        this.spinner.show('detail-bar-spinner');
        this.apiConfigurationsService.List().subscribe((x) => {
            let configurations = x.value.sort((a, b) => {
                return a.name < b.name ? -1 : 1;
            });
            componentInstance.apiConfigurations = configurations.filter((x) => x.contextOfUse === ContextOfUse.WorkflowAction);
            this.spinner.hide('detail-bar-spinner');
        });
    }

    /**
     * Charge les Triggers de type timer du workflow et renseigne le composant dynamique avec.
     * @param componentInstance : Instance du composant dynamique.
     */
    private loadTimerTriggers(componentInstance: NeedTimerTriggers) {
        this.spinner.show('detail-bar-spinner');
        componentInstance.timerTriggers = this.workflow.triggers.filter((x) => x.type === WorkflowTriggerTypes.Timer);
        this.spinner.hide('detail-bar-spinner');
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des états de dossier.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedFolderStates() {
        // Vérifie que le composant possède la propriété 'folderStates'.
        return 'folderStates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des clients.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedCustomers() {
        // Vérifie que le composant possède la propriété 'folderStates'.
        return 'customers' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des attributs.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedAttributes() {
        // Vérifie que le composant possède la propriété 'attributes'.
        return 'attributes' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des modèles de mail.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedEmailTemplates() {
        // Vérifie que le composant possède la propriété 'templates'.
        return 'templates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des modèles de SMS.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedSmsTemplates() {
        // Vérifie que le composant possède la propriété 'templates'.
        return 'smsTemplates' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des étapes.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedSteps() {
        // Vérifie que le composant possède la propriété 'steps'.
        return 'steps' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des configurations d'API.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedApiConfigurations() {
        // Vérifie que le composant possède la propriété 'apiConfigurations'.
        return 'apiConfigurations' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des triggers de type 'Timer'.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedTimerTriggers() {
        // Vérifie que le composant possède la propriété 'timerTriggers'.
        return 'timerTriggers' in this.componentRef.instance;
    }

    /**
     * Retourne une valeur indiquant si le composant dynamique a besoin de la liste des utilisateurs.
     * @returns True, s'il en a besoin; false autrement.
     */
    private IsComponentNeedUsers() {
        // Vérifie que le composant possède la propriété 'users'.
        return 'users' in this.componentRef.instance;
    }
}
